import { useState, useMemo } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Button,
  Checkbox,
  Card,
  CardBody,
  EmptyState as DSEmptyState,
  Skeleton,
  Tooltip,
} from '@kollektive/design-system';
import { Trash2, Mail, AlertTriangle, Search, RefreshCw, Clock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import type { Invitation, InvitationRevealResponse } from '@/hooks/useInvitations';
import styles from './InvitationsTable.module.css';

interface InvitationsTableProps {
  invitations: Invitation[];
  currentUserId: string;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  onCancel: (invitation: Invitation) => void;
  onResend: (invitation: Invitation) => void;
  onBulkCancel: (invitations: Invitation[]) => void;
  onReveal: (invitationId: string) => Promise<InvitationRevealResponse>;
  isCancelling?: boolean;
  isResending?: boolean;
  searchQuery?: string;
}

type SortField = 'email' | 'role' | 'created_at' | 'expires_at';
type SortDirection = 'asc' | 'desc';

// Helper functions
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRelativeExpiry(dateString: string, isExpired: boolean): string {
  if (isExpired) return 'Expired';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'Expires today';
  if (diffDays === 1) return 'Expires tomorrow';
  return `${diffDays} days`;
}

type RoleVariant = 'warning' | 'info' | 'neutral';

const roleConfig: Record<string, { label: string; variant: RoleVariant }> = {
  admin: { label: 'Admin', variant: 'warning' },
  member: { label: 'Member', variant: 'info' },
  viewer: { label: 'Viewer', variant: 'neutral' },
};

// Skeleton component for loading state
function TableSkeleton() {
  return (
    <Card>
      <CardBody className={styles.cardBody}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader style={{ width: 40 }}><Skeleton width={16} height={16} /></TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader>Invited By</TableHeader>
              <TableHeader>Sent</TableHeader>
              <TableHeader>Expires</TableHeader>
              <TableHeader style={{ width: 100 }}></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={16} height={16} /></TableCell>
                <TableCell><Skeleton width="10rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="4rem" height="1.25rem" /></TableCell>
                <TableCell><Skeleton width="6rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="5rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="4rem" height="1.25rem" /></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Skeleton width={24} height={24} />
                    <Skeleton width={24} height={24} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

export function InvitationsTable({
  invitations,
  currentUserId,
  isLoading,
  error,
  onRetry,
  onCancel,
  onResend,
  onBulkCancel,
  onReveal,
  isCancelling,
  isResending,
  searchQuery = '',
}: InvitationsTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [revealedEmails, setRevealedEmails] = useState<Record<string, string>>({});
  const [revealingIds, setRevealingIds] = useState<Set<string>>(new Set());

  // Filter invitations by search query
  const filteredInvitations = useMemo(() => {
    if (!searchQuery.trim()) return invitations;

    const query = searchQuery.toLowerCase();
    return invitations.filter((inv) =>
      inv.email?.toLowerCase().includes(query) ||
      inv.role.toLowerCase().includes(query) ||
      inv.invited_by.name?.toLowerCase().includes(query)
    );
  }, [invitations, searchQuery]);

  // Sort filtered invitations
  const sortedInvitations = useMemo(() => {
    return [...filteredInvitations].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'email':
          aVal = a.email || '';
          bVal = b.email || '';
          break;
        case 'role':
          const roleOrder = { admin: 0, member: 1, viewer: 2 };
          aVal = roleOrder[a.role as keyof typeof roleOrder] ?? 99;
          bVal = roleOrder[b.role as keyof typeof roleOrder] ?? 99;
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'expires_at':
          aVal = new Date(a.expires_at).getTime();
          bVal = new Date(b.expires_at).getTime();
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [filteredInvitations, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(sortedInvitations.map((inv) => inv.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (invId: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(invId);
    } else {
      newSet.delete(invId);
    }
    setSelectedIds(newSet);
  };

  const handleBulkCancel = () => {
    const invitationsToCancel = invitations.filter((inv) => selectedIds.has(inv.id));
    onBulkCancel(invitationsToCancel);
    setSelectedIds(new Set());
  };

  const handleResend = async (invitation: Invitation) => {
    setResendingId(invitation.id);
    try {
      await onResend(invitation);
    } finally {
      setResendingId(null);
    }
  };

  const handleReveal = async (invitationId: string) => {
    // If already revealed, toggle back to masked
    if (revealedEmails[invitationId]) {
      setRevealedEmails((prev) => {
        const newState = { ...prev };
        delete newState[invitationId];
        return newState;
      });
      return;
    }

    // Reveal the invitation email
    setRevealingIds((prev) => new Set(prev).add(invitationId));
    try {
      const revealed = await onReveal(invitationId);
      setRevealedEmails((prev) => ({ ...prev, [invitationId]: revealed.email }));
    } catch (err) {
      console.error('Failed to reveal invitation email:', err);
    } finally {
      setRevealingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  const allSelected = sortedInvitations.length > 0 && selectedIds.size === sortedInvitations.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < sortedInvitations.length;

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <DSEmptyState
        variant="error"
        icon={<AlertTriangle size={48} />}
        title="Failed to load invitations"
        description={error.message}
        action={onRetry ? <Button onClick={onRetry}>Try again</Button> : undefined}
      />
    );
  }

  if (invitations.length === 0) {
    return (
      <DSEmptyState
        icon={<Mail size={48} />}
        title="No pending invitations"
        description="Invite team members to collaborate on your workspace."
      />
    );
  }

  return (
    <div className={styles.container}>
      {filteredInvitations.length > 0 ? (
        <Card className={styles.tableCard}>
          <CardBody className={styles.cardBody}>
            <Table hoverable>
              <TableHead>
                <TableRow>
                  <TableHeader style={{ width: 40 }} className={styles.checkboxCell}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'email' ? sortDirection : null}
                    onClick={() => handleSort('email')}
                  >
                    Email
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'role' ? sortDirection : null}
                    onClick={() => handleSort('role')}
                  >
                    Role
                  </TableHeader>
                  <TableHeader>Invited By</TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'created_at' ? sortDirection : null}
                    onClick={() => handleSort('created_at')}
                  >
                    Sent
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'expires_at' ? sortDirection : null}
                    onClick={() => handleSort('expires_at')}
                  >
                    Expires
                  </TableHeader>
                  <TableHeader style={{ width: 100 }}></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInvitations.map((invitation) => {
                  const config = roleConfig[invitation.role] ?? { label: invitation.role, variant: 'neutral' as const };
                  const expiryLabel = formatRelativeExpiry(invitation.expires_at, invitation.is_expired);
                  const isExpired = invitation.is_expired;

                  return (
                    <TableRow key={invitation.id} selected={selectedIds.has(invitation.id)}>
                      <TableCell className={styles.checkboxCell}>
                        <Checkbox
                          checked={selectedIds.has(invitation.id)}
                          onChange={(e) => handleSelectOne(invitation.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className={styles.emailCell}>
                          <span className={styles.email}>
                            {revealedEmails[invitation.id] ?? invitation.email}
                          </span>
                          {invitation.email_status && (
                            invitation.email_status.sent ? (
                              <Tooltip content="Email sent successfully">
                                <span className={styles.emailSuccess}>
                                  <CheckCircle2 size={14} />
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip content={invitation.email_status.error || 'Email failed to send'}>
                                <span className={styles.emailError}>
                                  <AlertCircle size={14} />
                                </span>
                              </Tooltip>
                            )
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReveal(invitation.id)}
                            disabled={revealingIds.has(invitation.id)}
                            title={revealedEmails[invitation.id] ? 'Hide email' : 'Reveal full email'}
                            className={styles.revealButton}
                          >
                            {revealingIds.has(invitation.id) ? (
                              <Loader2 size={14} className={styles.spinning} />
                            ) : revealedEmails[invitation.id] ? (
                              <EyeOff size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={config.variant} styleVariant="subtle" size="sm">
                          {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={styles.invitedBy}>
                          {invitation.invited_by.name}
                          {invitation.invited_by.id === currentUserId && (
                            <span className={styles.youBadge}>(you)</span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(invitation.created_at)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={isExpired ? 'error' : 'neutral'}
                          styleVariant="subtle"
                          size="sm"
                        >
                          <Clock size={12} />
                          {expiryLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className={styles.actions}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResend(invitation)}
                            disabled={isResending || resendingId === invitation.id}
                            title="Resend invitation"
                            className={styles.actionButton}
                          >
                            <RefreshCw size={16} className={resendingId === invitation.id ? styles.spinning : ''} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onCancel(invitation)}
                            disabled={isCancelling}
                            title="Cancel invitation"
                            className={styles.cancelButton}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      ) : (
        <Card className={styles.tableCard}>
          <CardBody>
            <DSEmptyState
              variant="search"
              icon={<Search size={48} />}
              title="No results"
              description={`No invitations match "${searchQuery}"`}
            />
          </CardBody>
        </Card>
      )}

      {/* Bulk actions bar at bottom */}
      <div className={`${styles.bulkActions} ${selectedIds.size === 0 ? styles.bulkActionsHidden : ''}`}>
        <span className={styles.selectedCount}>
          {selectedIds.size} selected
        </span>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkCancel}
          disabled={isCancelling}
          iconLeft={<Trash2 size={16} />}
        >
          Cancel Selected
        </Button>
      </div>
    </div>
  );
}
